import { useEffect, useState } from "react";
import { FlattenedGadget } from "../../../models/GadgetModel";
import { Box, Tab, Tabs } from "@mui/material";
import { GadgetInformation } from "./GadgetInformation";
import { GadgetInformationDetails } from "./GadgetInformationDetails";
import { GadgetIOFormatContainer } from "./GadgetIOFormatContainer";
import { GadgetLicenseInfo } from "./GadgetLicenseInfo";
import { GadgetScreenshotInfo } from "./GadgetScreenshotInfo";
import styles from "../css/GadgetDetailsSection.module.css";
import { CustomTabPanel } from "./CustomTabPanel";
import { GadgetDetailsTopSection } from "./GadgetDetailsTopSection";
import { GadgetDaoFactory, GadgetUrlRequest, Method } from "../../Home/components/gadgets/GadgetRepository";
import { downloadFile } from "../../../helper/utils";
import { CustomModal } from "../../../components/Modal/CustomModal";
import { ConnectedDeviceList } from "./ConnectedDeviceList";
import { useGadgetInstallationProgress } from "../hooks/useGadgetInstallationProgress";
import { InstallationStatus } from "../../../helper/cloudcore/response/models/body";
import { useAppDispatch } from "../../../common/state/hooks";
import { update } from "../../../common/state/Slices/HeaderMenuSlice";
import { Routes } from "../../../routes/routes";
import { notify } from "../../../common/state/Slices/NotificationSlice";

interface GadgetDetailsSectionProps {
    displayGadget: FlattenedGadget,
    iconFile: string
}

export const GadgetDetailsSection = ({displayGadget, iconFile}: GadgetDetailsSectionProps) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const { checkInstallationStatus } = useGadgetInstallationProgress();
  const selectedTabStyle = {
    '&.Mui-selected': {
      color: '#44bcdb'
    },
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
        // Included for legacy support, e.g. Chrome/Edge < 119
      event.returnValue = true;
    };
    const preventReload = () => window.addEventListener('beforeunload', handleBeforeUnload);
    checkInstallationStatus([InstallationStatus.INSTALLING], preventReload, []);
    
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [checkInstallationStatus]);

  useEffect(() => {
    dispatch(
      update({
        selectedMenu: Routes.GADGET_DETAILS
      })
    );
  }, [dispatch]);

  const handleGadgetDownload = () => {
    const gadgeturlRequest = {
      version: displayGadget.version,
      method: Method.GET,
      platform: displayGadget.platform
    } as GadgetUrlRequest;

    GadgetDaoFactory.getGadgetRepository()
      .getPreSignedUrl(displayGadget.id, gadgeturlRequest)
      .then((result) => {
        downloadFile(result.url, `${displayGadget.name}.zip`);
    })
    .catch(() => {
        const errorMessage = "You are not authorized to download this gadget";
        dispatch(
            notify({
                message: errorMessage,
                severity: "error"
            })
        );
        console.error(errorMessage);
    });
  }
  
  const handleChange = (event: React.SyntheticEvent, newValue: 0) => setValue(newValue);


  return (
    <>
      <GadgetDetailsTopSection 
        displayGadget={displayGadget} 
        iconFile={iconFile} 
        handleOpenModal={handleOpenModal} 
        handleGadgetDownload={handleGadgetDownload}
      />
      <Box className={styles.tabContainer}>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs example"
          TabIndicatorProps={{
            style: {
              backgroundColor: '#44bcdb'
            },
          }}
          centered
        >
          <Tab value={0} label="Agent Information" sx={selectedTabStyle} data-testid={"gadget-info-tab"}/>
          <Tab value={1} label="Agent Format" sx={selectedTabStyle} data-testid={"gadget-format-tab"}/>
          <Tab value={2} label="Agent License" sx={selectedTabStyle} data-testid={"gadget-license-info-tab"}/>
          <Tab value={3} label="Screenshot" sx={selectedTabStyle} data-testid={"gadget-screenshots-tab"}/>
        </Tabs>
        <CustomTabPanel value={value} index={0}>
          <GadgetInformation
            children={<GadgetInformationDetails gadget={displayGadget}/>}
            headerText={'Agent Information'}
            infoText={'Overview'}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
        <GadgetInformation 
            children={<GadgetIOFormatContainer gadget={displayGadget}/>}
            headerText={'Agent Format'}
            infoText={'I/O Format Overview'}
        />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
        <GadgetInformation 
            children={<GadgetLicenseInfo license={displayGadget.license}/>}
            headerText={'Agent License'}
            infoText={'License Details'}
        />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
        <GadgetInformation 
            children={<GadgetScreenshotInfo gadget={displayGadget}/>}
            headerText={'Screenshot'}
            infoText={'Agent Screenshots'}
        />
        </CustomTabPanel>
      </Box>
      <CustomModal 
        open={openModal} 
        handleClose={handleCloseModal} 
        children={<ConnectedDeviceList displayGadget={displayGadget}/>}
      />
    </>
  )
}